import { FileDropzone } from "@/components/FileDropzone";
import { toaster } from "@/components/ui/toaster";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  HStack,
  Portal,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AlertCircle, CheckCircle, Download, Info } from "lucide-react";
import { useMerchant } from "@/hooks/useMerchant";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/config/routes";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { useTopUpsEnabled } from "../hooks/useTopUpsEnabled";
import { useUploadDispersionFile } from "../hooks/useUploadDispersionFile";
import { mapPayoutBatchType } from "../mappers/payouts.mapper";
import type { PayoutBatchType } from "../types/payouts.type";

const BATCH_TYPE_OPTIONS: {
  value: PayoutBatchType;
  title: string;
  description: string;
  gmfNote: string;
}[] = [
  {
    value: "owner",
    title: mapPayoutBatchType.owner.label,
    description: "Canon de arrendamiento.",
    gmfNote: "Exento del 4x1000.",
  },
  {
    value: "buildingManagement",
    title: mapPayoutBatchType.buildingManagement.label,
    description: "Cuotas de administración.",
    gmfNote: "Sujeto al 4x1000.",
  },
];

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(amount);

export default function UploadFileModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { merchant } = useMerchant();
  const { isAdmin } = usePermissions();
  // top ups already paid GMF when the merchant loaded them
  const isTopUpsEnabled = useTopUpsEnabled();
  const isBuildingManagementEnabled =
    !!merchant?.payoutsConfig?.buildingManagementEnabled;

  const [step, setStep] = useState<"type" | "file">("type");
  const [batchType, setBatchType] = useState<PayoutBatchType | null>(null);
  const [exemptionDeclared, setExemptionDeclared] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const hasSubmittedRef = useRef(false);
  const { uploadFile, uploadStatus, resetUpload, isUploading } =
    useUploadDispersionFile();
  const navigate = useNavigate();

  const isProcessing =
    uploadStatus.status === "processing" || uploadStatus.status === "uploading";
  const rowErrors =
    uploadStatus.status === "success"
      ? uploadStatus.errors
      : uploadStatus.status === "failed"
      ? uploadStatus.errors
      : undefined;
  const hasRowErrors = (rowErrors?.length ?? 0) > 0;
  const isSuccess =
    hasSubmittedRef.current &&
    uploadStatus.status === "success" &&
    !hasRowErrors;
  const isPartialError =
    hasSubmittedRef.current &&
    uploadStatus.status === "success" &&
    hasRowErrors;
  const isError = hasSubmittedRef.current && uploadStatus.status === "failed";
  const showResult = isSuccess || isPartialError || isError;
  // Only admins of merchants with building management see the type step.
  // Everyone else always uploads an owner batch, exactly as before.
  const showTypeStep = isBuildingManagementEnabled && isAdmin;
  const currentStep = showTypeStep ? step : "file";
  const currentBatchType: PayoutBatchType | null = showTypeStep
    ? batchType
    : "owner";
  const canContinueToFile = !!batchType;
  const canUpload =
    !!selectedFile &&
    (currentBatchType === "buildingManagement" ||
      exemptionDeclared ||
      isTopUpsEnabled);

  const handleClose = () => {
    if (!isProcessing) {
      resetUpload();
      setSelectedFile(null);
      setStep("type");
      setBatchType(null);
      setExemptionDeclared(false);
      hasSubmittedRef.current = false;
    }
    onClose();
  };

  const handleSelectType = (value: PayoutBatchType) => {
    setBatchType(value);
    if (value !== "owner") setExemptionDeclared(false);
  };

  const handleUpload = async () => {
    if (!selectedFile || !currentBatchType || !canUpload) return;

    resetUpload();
    hasSubmittedRef.current = true;
    try {
      await uploadFile(selectedFile, {
        type: currentBatchType,
        exemptionDeclared,
      });
    } catch {
      toaster.create({
        title: "Error",
        description: "Hubo un error al subir el archivo.",
        type: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Dialog.Root
      lazyMount
      open={isOpen}
      onOpenChange={(e) => !e.open && handleClose()}
      size="lg"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" p={4}>
              <HStack justify="space-between" align="center" w="full" pr={8}>
                <Dialog.Title fontSize="lg" fontWeight="semibold">
                  {currentStep === "file" && currentBatchType && showTypeStep
                    ? `Cargar archivo · ${mapPayoutBatchType[currentBatchType].label}`
                    : "Cargar archivo de dispersión"}
                </Dialog.Title>
                {!isProcessing && !showResult && currentStep === "file" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const template =
                        currentBatchType === "buildingManagement"
                          ? "pagos_admin_plantilla.xlsx"
                          : "pagos_plantilla.xlsx";
                      const link = document.createElement("a");
                      link.href = `/${template}`;
                      link.download = template;
                      link.click();
                    }}
                  >
                    <Download size={16} />
                    Descargar plantilla
                  </Button>
                )}
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger position="top-end" top={3} />
            <Dialog.Body py={6}>
              {isProcessing ? (
                <VStack gap={4} py={8}>
                  <Spinner size="lg" color="palomma.500" />
                  <Text fontWeight="medium" color="gray.700">
                    {uploadStatus.status === "uploading"
                      ? "Subiendo archivo..."
                      : "Procesando archivo..."}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Esto puede tomar unos segundos
                  </Text>
                </VStack>
              ) : isPartialError ? (
                <VStack gap={4} align="stretch" py={2}>
                  <VStack gap={3}>
                    <Box p={4} bg="red.50" borderRadius="full">
                      <AlertCircle size={40} color="#e53e3e" />
                    </Box>
                    <Text fontWeight="medium" color="red.600">
                      El archivo se procesó con errores
                    </Text>
                  </VStack>
                  <HStack gap={6} px={1} flexWrap="wrap">
                    <HStack>
                      <Text fontSize="sm" fontWeight={600}>
                        Giros creados:
                      </Text>
                      <Text fontSize="sm" color="green.600">
                        {uploadStatus.totalCount}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight={600}>
                        Errores:
                      </Text>
                      <Text fontSize="sm" color="red.600">
                        {rowErrors?.length ?? 0}
                      </Text>
                    </HStack>
                  </HStack>
                  {hasRowErrors && (
                    <Box w="full" overflowY="auto" maxHeight="280px">
                      <VStack w="full" gap={2} align="stretch">
                        {rowErrors?.map((error, idx) => (
                          <Box
                            key={idx}
                            p={3}
                            bg="red.50"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="red.200"
                          >
                            <Text
                              fontSize="xs"
                              color="red.500"
                              fontWeight="bold"
                              mb={1}
                            >
                              Fila {error.index + 1}: {error.requestLine}
                            </Text>
                            <Text fontSize="sm" color="red.700">
                              {error.errorMessage}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              ) : isSuccess ? (
                <VStack gap={4} align="stretch" py={2}>
                  <VStack gap={3}>
                    <Box p={4} bg="green.50" borderRadius="full">
                      <CheckCircle size={40} color="#38a169" />
                    </Box>
                    <Text fontWeight="medium" color="green.600">
                      El lote de dispersión se ha creado exitosamente
                    </Text>
                  </VStack>
                  <HStack gap={6} px={1} flexWrap="wrap">
                    <HStack>
                      <Text fontSize="sm" fontWeight={600}>
                        Giros:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {uploadStatus.totalCount}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight={600}>
                        Monto total:
                      </Text>
                      <Text fontSize="sm" color="green.600">
                        {formatCurrency(uploadStatus.totalAmount)}
                      </Text>
                    </HStack>
                  </HStack>
                </VStack>
              ) : isError ? (
                <VStack gap={4} py={4}>
                  <Box p={4} bg="red.50" borderRadius="full">
                    <AlertCircle size={40} color="#e53e3e" />
                  </Box>
                  <Text fontWeight="medium" color="red.600">
                    Error al procesar el archivo
                  </Text>
                  <Box w="full" p={4} bg="red.50" borderRadius="md">
                    <Text fontSize="sm" color="gray.700">
                      {uploadStatus.error}
                    </Text>
                  </Box>
                  {hasRowErrors && (
                    <Box w="full" overflowY="auto" maxHeight="280px">
                      <VStack w="full" gap={2} align="stretch">
                        {rowErrors?.map((error, idx) => (
                          <Box
                            key={idx}
                            p={3}
                            bg="red.50"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="red.200"
                          >
                            <Text
                              fontSize="xs"
                              color="red.500"
                              fontWeight="bold"
                              mb={1}
                            >
                              Fila {error.index + 1}: {error.requestLine}
                            </Text>
                            <Text fontSize="sm" color="red.700">
                              {error.errorMessage}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              ) : currentStep === "type" ? (
                <VStack gap={4} align="stretch">
                  <Text fontSize="sm" color="gray.600">
                    ¿Qué tipo de pagos contiene este lote?
                  </Text>
                  {BATCH_TYPE_OPTIONS.map((option) => {
                    const isSelected = batchType === option.value;
                    return (
                      <Box
                        key={option.value}
                        p={4}
                        border="1px solid"
                        borderColor={isSelected ? "palomma.400" : "gray.200"}
                        bg={isSelected ? "palomma.50" : "white"}
                        borderRadius="lg"
                        cursor="pointer"
                        transition="all 0.2s"
                        _hover={{ borderColor: "palomma.400" }}
                        onClick={() => handleSelectType(option.value)}
                      >
                        <Text fontWeight="medium" color="gray.800">
                          {option.title}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {isTopUpsEnabled
                            ? option.description
                            : `${option.description} ${option.gmfNote}`}
                        </Text>
                      </Box>
                    );
                  })}
                </VStack>
              ) : (
                <>
                  <FileDropzone
                    file={selectedFile}
                    onChange={setSelectedFile}
                    accept=".csv,.xlsx,.txt"
                    hint="Soporta archivos .CSV, .XLSX y .TXT"
                  />

                  {currentBatchType === "owner" && !isTopUpsEnabled && (
                    <Box
                      mt={4}
                      p={4}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Checkbox.Root
                        checked={exemptionDeclared}
                        onCheckedChange={(e) =>
                          setExemptionDeclared(!!e.checked)
                        }
                        alignItems="flex-start"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control mt={1} />
                        <Checkbox.Label fontSize="sm" color="gray.700">
                          Declaro que este lote contiene únicamente cánones de
                          arrendamiento, exentos del gravamen a los movimientos
                          financieros (4x1000).
                        </Checkbox.Label>
                      </Checkbox.Root>
                      {showTypeStep && (
                        <Text fontSize="sm" color="gray.600" mt={3} pl={7}>
                          ¿Son cuotas de administración?{" "}
                          <Button
                            variant="ghost"
                            size="sm"
                            colorPalette="palomma"
                            p={0}
                            minW="auto"
                            h="auto"
                            fontWeight="medium"
                            onClick={() =>
                              handleSelectType("buildingManagement")
                            }
                          >
                            Cambiar a Administraciones
                          </Button>
                        </Text>
                      )}
                    </Box>
                  )}

                  {currentBatchType === "buildingManagement" &&
                    !isTopUpsEnabled && (
                      <Box
                        mt={4}
                        p={4}
                        bg="orange.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="orange.200"
                      >
                        <HStack align="flex-start" gap={3}>
                          <Box color="orange.500" mt="2px">
                            <Info size={18} />
                          </Box>
                          <Text fontSize="sm" color="orange.800">
                            Este lote causa el gravamen a los movimientos
                            financieros (4x1000). El GMF corre por cuenta del
                            comercio y se descuenta de tu saldo junto con el
                            monto y la comisión.
                          </Text>
                        </HStack>
                        {showTypeStep && (
                          <Text fontSize="sm" color="orange.800" mt={3} pl={7}>
                            ¿Son cánones de arrendamiento?{" "}
                            <Button
                              variant="ghost"
                              size="sm"
                              colorPalette="palomma"
                              p={0}
                              minW="auto"
                              h="auto"
                              fontWeight="medium"
                              onClick={() => handleSelectType("owner")}
                            >
                              Cambiar a Propietarios
                            </Button>
                          </Text>
                        )}
                      </Box>
                    )}
                </>
              )}
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80" p={4}>
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={isUploading}
              >
                {showResult ? "Cerrar" : "Cancelar"}
              </Button>
              {(isSuccess || isPartialError) &&
                uploadStatus.status === "success" && (
                  <Button
                    colorPalette="palomma"
                    onClick={() => {
                      const path = ROUTES.DASHBOARD.PAYOUTS_BATCH.replace(
                        ":batchId",
                        uploadStatus.batchId
                      );
                      handleClose();
                      navigate(path);
                    }}
                  >
                    Ver lote
                  </Button>
                )}
              {!isProcessing && !showResult && currentStep === "type" && (
                <Button
                  colorPalette="palomma"
                  onClick={() => setStep("file")}
                  disabled={!canContinueToFile}
                >
                  Continuar
                </Button>
              )}
              {!isProcessing &&
                !showResult &&
                currentStep === "file" &&
                showTypeStep && (
                  <Button
                    variant="ghost"
                    onClick={() => setStep("type")}
                    disabled={isUploading}
                  >
                    Atrás
                  </Button>
                )}
              {!isProcessing && !showResult && currentStep === "file" && (
                <Button
                  colorPalette="palomma"
                  onClick={handleUpload}
                  loading={isUploading}
                  disabled={!canUpload}
                >
                  Cargar Lote
                </Button>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
